import type { UseQueryResult } from "react-query";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";

import { AnalyticsGA } from "../../utils/AnalyticsGA";
import apiBase from "../../utils/apiBase";
import type { IAppMetrics } from "./useBilling";

export interface IApplication {
  id: string;
  name: string;
  settings?: Record<string, number | string>;
  tokenMetrics?: IApplicationTokenMetrics;
  created?: number;
}

export interface IApplicationTokenMetrics {
  cachedRequestCount: number;
  openAiRequestCount: number;
  perModel: {
    [key: string]: {
      cached: {
        inputTokens: number;
        outputTokens: number;
      };
      active: {
        inputTokens: number;
        outputTokens: number;
      };
    };
  };
}

interface IApplicationRequest {
  orgId: string;
  projId: string;
  newName?: string;
  settings?: Record<string, number | string>;
}

const fetchApplications = async (
  organizationId: string,
): Promise<IApplication[]> => {
  return await apiBase.get(`/organizations/${organizationId}/applications`);
};

const fetchSingleApplication = async (
  organizationId: string,
  applicationId: string,
): Promise<IApplication | null> => {
  return await apiBase.get(
    `/organizations/${organizationId}/applications/${applicationId}/token-metrics`,
  );
};

interface CreateApplicationRequest {
  application: {
    name: string;
    settings?: Record<string, number | string>;
  };
}

const createApplication = async (data: IApplication): Promise<IApplication> => {
  const req: CreateApplicationRequest = {
    application: {
      name: data.name,
    },
  };

  if (data.settings) {
    req.application.settings = data.settings;
  }

  return await apiBase.post(`/organizations/${data.id}/applications`, req);
};

const deleteApplication = async (data: IApplicationRequest) => {
  return await apiBase.delete(
    `/organizations/${data.orgId}/applications/${data.projId}`,
  );
};

const updateApplication = async (data: IApplicationRequest) => {
  const req: CreateApplicationRequest = {
    application: {
      name: data.newName || "",
    },
  };
  if (data.settings) {
    req.application.settings = data.settings;
  }

  return await apiBase.patch(
    `/organizations/${data.orgId}/applications/${data.projId}`,
    req,
  );
};

export const useCreateApplication = () => {
  const queryClient = useQueryClient();
  return useMutation(createApplication, {
    onSuccess: () => {
      queryClient.invalidateQueries("applications").catch(console.error);
      toast.success("Project created successfully");
      AnalyticsGA.trackEvent(
        "Project",
        "Create",
        "Project created successfully",
      );
    },
  });
};

export const useApplications = (
  organizationId: string,
): UseQueryResult<IApplication[], unknown> => {
  const { pathname } = useLocation();
  return useQuery(
    ["applications", organizationId, pathname],
    () => fetchApplications(organizationId),
    {
      enabled: !!organizationId,
    },
  );
};

export const useSingleApplication = (
  organizationId: string,
  applicationId: string,
): UseQueryResult<IAppMetrics, unknown> => {
  const { pathname } = useLocation();
  return useQuery(
    ["singleApplication", organizationId, pathname, applicationId],
    () => fetchSingleApplication(organizationId, applicationId),
    {
      enabled: !!organizationId,
    },
  );
};

export const useDeleteApplication = () => {
  const queryClient = useQueryClient();
  return useMutation(deleteApplication, {
    onSuccess: () => {
      queryClient.invalidateQueries("applications").catch(console.error);
      toast.success("Project deleted successfully");
      AnalyticsGA.trackEvent(
        "Project",
        "Delete",
        "Project deleted successfully",
      );
    },
  });
};

export const useUpdateApplication = () => {
  const queryClient = useQueryClient();
  return useMutation(updateApplication, {
    onSuccess: () => {
      queryClient.invalidateQueries("applications").catch(console.error);
      toast.success("Project updated successfully");
    },
  });
};
